import {Inject, Injectable} from '@angular/core';
import {CommonRzmService} from './common-rzm.service';
import {HttpClient} from '@angular/common/http';
import {from, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Mfa} from '@iana-rzms/bff-sdk';
import {User, Commands, ApiTokens, AdminAction} from '@iana-rzms/bff-sdk';
import {ServerConstants} from '../model/ServerConstants';
import {RestEndPoints} from '../model/RestEndPoints';
import {UserObject} from '@iana-rzms/bff-sdk';
import {AuthService} from './auth.service';
import {CommandEnvelope} from '../generate/CommandEnvelope';
import {AppConfigService} from './app.config.service';
import {ApiTokenCreatedResponse} from "../components/user-management/api-tokens/api-token.model";
import {UserApiTokenMetadata} from '@iana-rzms/bff-sdk';
import {BffClientService} from './bff-client.service';
import {MfaRecoveryCodeResponse} from "../model/mfaRecoveryCodeResponse";

@Injectable({
  providedIn: 'root'
})
export class UserService extends CommonRzmService {

  constructor(
    @Inject(HttpClient) http,
    @Inject('env') environment,
    @Inject(AuthService) auth,
    @Inject(AppConfigService) private config: AppConfigService,
    bffClientService: BffClientService
  ) {
    super(http, environment, auth, bffClientService);
  }

  getTldManagerUserByEmail(email: string): Observable<UserObject> { //this is only being used while adding user in auth policy
    return from(User.getUserByEmail(this.sdkOptions({
      responseStyle: 'data',
      path: { email }
    }))).pipe(map(res => res as UserObject));
  }

  getCurrentUser() {
    return from(User.getCurrentUser(this.sdkOptions({responseStyle: 'data'}))).pipe(
      map(response => (response as any)?.data ?? (response as any))
    );
  }

  retrieveCurrentUserAuditLog() {
    return from(User.getCurrentUserAuditLog(this.sdkOptions({responseStyle: 'data'}))).pipe(
      map(response => (response as any)?.data ?? (response as any) ?? [])
    );
  }

  createOrEditUser(user: UserObject, signUpMode: boolean) {
    if (!signUpMode) {
      return this.updateUser(user);
    } else {
      return this.completeSignUp(user);
    }

  }

  updateUser(user: UserObject) {
    return from(User.updateUserById(this.sdkOptions({
      path: { userId: user.id },
      body: user
    })));
  }

  completeSignUp(user: UserObject) {
    // Do NOT set termsAndPrivacyAcknowledgmentDate here.
    // New users must explicitly accept the ToS via the popup after signup.
    return from(User.completeUserSignup(this.sdkOptions({
      body: user
    })));
  }

  inviteUser(user: UserObject) {
    const body: any = {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      ...(this.isCurrentUserAdmin() && { role: user.roles?.[0] })
    };

    const apiCall = this.isCurrentUserAdmin()
        ? AdminAction.adminCreateUser
        : User.createUser;

    return from(apiCall(this.sdkOptions({
      responseStyle: 'data',
      throwOnError: true,
      body
    }))).pipe(map(res => (res as any)?.data ?? res));
  }

  changePassword(currentPassword: string, newPassword: string) {
    return from(User.changePassword(this.sdkOptions({
      responseStyle: 'data',
      body: {
        currentPassword,
        newPassword
      }
    }))).pipe(map(res => (res as any)?.data ?? res));
  }

  setup2FA(): Observable<{ uri: string }> {
    return from(Mfa.initiateMfa(this.sdkOptions({
      responseStyle: 'data'
    }))).pipe(
      map(res => ({ uri: (res as any).uri }))
    );
  }

  verify2FA(code: string): Observable<any> {
    const body = { code };
    const options = this.sdkOptions({
      body: body,
      headers: { 'Content-Type': 'application/json' }
    });

    return from(Mfa.activateMfa(options));
  }

  disable2FA(): Observable<any> {
    return from(Mfa.disableMfa(this.sdkOptions({})));
  }

  changeExpiredPasswordLegacy(currentPassword: string, newPassword: string) {
    const changePasswordCommand = {
      currentPassword,
      newPassword
    };
    const commandEnvelope = this.createCommandEnvelope(changePasswordCommand, ServerConstants.CHANGE_PASSWORD_COMMAND);
    return from(Commands.command(this.sdkOptions({
      responseStyle: 'data',
      url: RestEndPoints.COMMON_COMMAND,
      body: commandEnvelope
    }))).pipe(map(res => (res as any)?.data ?? res));
  }

  changeExpiredPassword(commandEnvelope: CommandEnvelope) {
    return from(Commands.command(this.sdkOptions({
      responseStyle: 'data',
      url: RestEndPoints.COMMON_COMMAND,
      body: commandEnvelope
    }))).pipe(map(res => (res as any)?.data ?? res));
  }

  acknowledgeTerms(): Observable<any> {
    return from(User.acceptCurrentTerms(this.sdkOptions({
      responseStyle: 'data'
    }))).pipe(map(res => (res as any)?.data ?? res));
  }

  generateRecoveryCodes(): Observable<MfaRecoveryCodeResponse> {
    return from(Mfa.generateRecoveryCodes(this.sdkOptions({
      responseStyle: 'data'
    }))).pipe(
      map(res => {
        const codes = (res as any)?.data?.recoveryCodes || (res as any)?.recoveryCodes || [];
        return { mfaRecoveryCodes: codes };
      })
    );
  }

  getExistingApiTokens(userId: string): Observable<UserApiTokenMetadata[]> {
    return from(ApiTokens.getApiTokensMetaData(this.sdkOptions({
      responseStyle: 'data',
      path: { userId }
    }))).pipe(
      map(response => {
        return Array.isArray(response) ? response as UserApiTokenMetadata[] : [];
      })
    );
  }

  createApiToken(userId: string): Observable<ApiTokenCreatedResponse> {
    return from(ApiTokens.generateApiToken(this.sdkOptions({
      responseStyle: 'data'
    }))).pipe(map(res => {
      const token = (res as any)?.data?.token || (res as any)?.token;
      return { jwt: token } as ApiTokenCreatedResponse;
    }));
  }

  renameApiToken(sessionId: string, tokenName: string): Observable<any> {
    return from(ApiTokens.renameApiToken(this.sdkOptions({
      responseStyle: 'data',
      body: { tokenSessionId: sessionId, tokenName }
    })));
  }

  revokeApiToken(sessionId: string, tokenSuffix: string): Observable<any> {
    return from(ApiTokens.revokeApiToken(this.sdkOptions({
      responseStyle: 'data',
      body: { tokenSessionId: sessionId, tokenSuffix }
    })));
  }

  updateCommunicationSettings(userId: string, operationalAlerts: boolean, featureUpdates: boolean, technicalAlerts: boolean) {
    return from(User.updateUserCommunicationPreferences(this.sdkOptions({
      responseStyle: 'data',
      path: { userId },
      body: {
        operational_alerts: operationalAlerts,
        feature_updates: featureUpdates,
        technical_alerts: technicalAlerts
      }
    })));
  }
}
